import { useMemo } from "react"
import { isConnectorVersionUpToDate } from "~/ui-rtk/utils/connector-version-utils"
import useConnectors from "~/ui-rtk/hooks/connectors"
import useConnectedServices from "~/ui-rtk/hooks/connected-services"
import { ConnectionStatus } from "~/ui-rtk/api/types/company-connector.types"

interface ConnectorUpdateAlert {
  hasUpdates: boolean
  updateCount: number
  connectorsNeedingUpdate: Array<{
    id: string
    name: string
    currentVersion?: number
    companyVersion?: number
    categories: string[]
  }>
  recommendedCategory?: string
}

/**
 * Custom hook to detect connectors that need version updates
 *
 * This hook checks all connected services against their current connector definitions
 * to identify when scope versions are mismatched, indicating an update is available.
 *
 * @returns ConnectorUpdateAlert - object with update information
 */
export const useConnectorUpdateAlert = (): ConnectorUpdateAlert => {
  const { connectors: dataConnectors } = useConnectors()
  const { connectedServices } = useConnectedServices()

  return useMemo(() => {
    if (!dataConnectors || !connectedServices) {
      return {
        hasUpdates: false,
        updateCount: 0,
        connectorsNeedingUpdate: [],
      }
    }

    const connectorsNeedingUpdate = []

    for (const connectedService of connectedServices) {
      // Only check connected services (not failed, pending, etc.)
      if (connectedService.status !== ConnectionStatus.CONNECTED) {
        continue
      }

      // Find the corresponding connector definition
      const connector = dataConnectors?.find(
        (c: { id: string }) => c.id === connectedService.serviceName,
      )

      if (!connector) {
        continue
      }

      // Check if version is up to date
      const isUpToDate = isConnectorVersionUpToDate(connector, connectedService)

      if (!isUpToDate) {
        connectorsNeedingUpdate.push({
          id: connector.id,
          name: connector.name,
          currentVersion: connector.scopeVersion,
          companyVersion: connectedService.scopeVersion,
          categories: connector.category || [],
        })
      }
    }

    // Determine recommended category - prioritize "Essential", then most common category
    let recommendedCategory: string | undefined
    if (connectorsNeedingUpdate.length > 0) {
      // Check if any connectors are in "Essential" category
      const hasEssential = connectorsNeedingUpdate.some(c =>
        c.categories.includes("Essential"),
      )

      if (hasEssential) {
        recommendedCategory = "Essential"
      } else {
        // Find most common category
        const categoryCount: Record<string, number> = {}
        connectorsNeedingUpdate.forEach(connector => {
          connector.categories.forEach(category => {
            categoryCount[category] = (categoryCount[category] || 0) + 1
          })
        })

        recommendedCategory = Object.keys(categoryCount).reduce(
          (a, b) => (categoryCount[a] > categoryCount[b] ? a : b),
          Object.keys(categoryCount)[0],
        )
      }
    }

    return {
      hasUpdates: connectorsNeedingUpdate.length > 0,
      updateCount: connectorsNeedingUpdate.length,
      connectorsNeedingUpdate,
      recommendedCategory,
    }
  }, [dataConnectors, connectedServices])
}

export default useConnectorUpdateAlert
